import {IBeginPaymentStrategyOption, IPaymentStrategy} from "./payment-strategy.interface";
import {IPaymentStatusResponse} from "../../../../airline-webapi/responses/payment-status.response";
import {ILoadPaymentMethodsOptions, IStartBookingPaymentResult} from "../payment-handler.view-model.interface";
import {PaymentMethodFactory} from "../methods/payment-method-factory";
import {BookingModel} from "../../booking.model";
import {IServiceFactory} from "../../../../service-factory.interface";
import {IBookingPaymentMethodsResponse} from "../../../../airline-webapi/responses/booking-payment-methods.response";
import {BookingSessionStorageKeys} from "../../storage/booking-session-storage-keys";
import {makeObservable, observable, runInAction} from "mobx";
import {IBeginBookingPaymentResponse} from "../../../../airline-webapi/responses/begin-booking-payment.response";
import {IBeginBookingPaymentRequest} from "../../../../airline-webapi/requests/begin-booking-payment.request";
import {IFinalizePspOrderResponse} from "../../../../airline-webapi/responses/finalize-psp-order.response";
import {ThreeDSMethodGatheringHandler} from "../threeds-method-gathering.handler";
import {IPaymentMethodModel} from "../methods/payment-method.model.interface";
import {IInsurancePaymentPageQuotesRequest} from "../../../../airline-webapi/requests/booking-payment-methods.request";

export class WebapiPaymentStrategy implements IPaymentStrategy {
    constructor(private readonly booking: BookingModel) {
        const paymentMethodsResponse = this.booking.storage.getJson<IBookingPaymentMethodsResponse>(BookingSessionStorageKeys.bookingPaymentMethods);

        if(paymentMethodsResponse) {
            this._allPaymentMethods = this._createPaymentMethods(paymentMethodsResponse);
        }


        makeObservable<this, '_allPaymentMethods'>(this, {
            _allPaymentMethods: observable.ref
        });
    }


    private get services(): IServiceFactory {
        return this.booking.services;
    }

    private get _pendingPaymentDetails(): IBeginBookingPaymentResponse | null {
        return this.booking.storage.getJson<IBeginBookingPaymentResponse>(BookingSessionStorageKeys.pendingPaymentDetails) ?? null;
    }

    private set _pendingPaymentDetails(value: IBeginBookingPaymentResponse | null) {
        if(value) {
            this.booking.storage.setJson(BookingSessionStorageKeys.pendingPaymentDetails, value);
        } else {
            this.booking.storage.removeItem(BookingSessionStorageKeys.pendingPaymentDetails);
        }

    }

    get isRedirectInProgress(): boolean {
        return Boolean(this._pendingPaymentDetails?.redirectUrl);
    }

    async loadPaymentMethods(options: ILoadPaymentMethodsOptions): Promise<void> {
        let paymentMethodsResponse: IBookingPaymentMethodsResponse;

        let insuranceQuotesRequest: IInsurancePaymentPageQuotesRequest | null = null;
        try {
            insuranceQuotesRequest = await this.booking.insurance.buildPaymentPageQuotesRequest();
        } catch (err) {
            this.services.logger.error('Failed to build insurance payment page quotes request', err);
        }

        const executeGetBookingPaymentMethods = async () => {


            return  await this.services.airlineWebapi.getBookingPaymentMethods({
                dotRezToken: this.booking.token,
                insuranceQuotesRequest: insuranceQuotesRequest
            });
        }

        if(options.showLoadingIndicator) {
            paymentMethodsResponse = await this.services.loadingIndicator.execute({
                action: async () => {
                    return await executeGetBookingPaymentMethods();
                }
            })
        } else {
            paymentMethodsResponse = await executeGetBookingPaymentMethods();
        }


        const paymentMethods = this._createPaymentMethods(paymentMethodsResponse);
        runInAction(() => {
            this._allPaymentMethods = paymentMethods;
            this.booking.storage.setJson(BookingSessionStorageKeys.bookingPaymentMethods, paymentMethodsResponse);
        })

        if(insuranceQuotesRequest) {
            this.booking.insurance.setPaymentPageQuotes(paymentMethodsResponse.insuranceQuotes);
        }

    }


    private _createPaymentMethods(paymentMethodsResponse: IBookingPaymentMethodsResponse): IPaymentMethodModel[] {

        const result: IPaymentMethodModel[] = [];

        const existingPaymentMethods = this._allPaymentMethods.toDictionary(pm => pm.code);

        paymentMethodsResponse.paymentMethods.forEach(pm => {
            const existingPM = existingPaymentMethods[pm.code];
            if(existingPM) {
                existingPM.update(pm.pspCode, PaymentMethodFactory.createSupportedCardNetworksModels(pm.supportedCardNetworks ?? []));
                result.push(existingPM);
            } else {
                const newPM = PaymentMethodFactory.createPaymentMethod(this.booking, pm.code, pm.pspCode, pm.supportedCardNetworks);
                if(newPM) {
                    result.push(newPM);
                }
            }

        });


        return result.sort((p1, p2) => p1.getSortOrder() - p2.getSortOrder());


    }

    private _allPaymentMethods: IPaymentMethodModel[] = [];
    get allPaymentMethods(): IPaymentMethodModel[] {
        return this._allPaymentMethods;
    }


    private _methodGatheringHandler: ThreeDSMethodGatheringHandler | null = null;
    private _destroyMethodGatheringHandler(): void {
        this._methodGatheringHandler?.dispose();
        this._methodGatheringHandler = null;
    }

    async beginPayment(options: IBeginPaymentStrategyOption): Promise<IStartBookingPaymentResult> {
        let beginPaymentRequest: IBeginBookingPaymentRequest = {
            dotRezToken: this.booking.token,
            bookingTimestamp: this.booking.bookingTimestamp,
            attempt: options.paymentAttempt,
            paymentMethodCode: options.selectedMethod.code,
            pspCode: options.selectedMethod.pspCode,
            culture: this.services.language.currentLanguage,
            callbacks: this.booking.bookingStrategyAdapter.getPaymentCallbacks(),
            vouchers: this.booking.paymentHandler.voucher.getAppliedVoucherForBeginPayment(),
            organizationCredit: this.booking.paymentHandler.organizationCredit.used.amount,
            bookingFlow: this.booking.bookingStrategyAdapter.bookingFlowType,
            agentName: this.booking.bookingStrategyAdapter.agentName,
            browserDetails: options.browserDetails,
            useIframeForRedirect: options.useIframeForRedirect,
            psoWorkersAgreements: this.booking.pso.workersAgreements.agreements.map(worker => worker.buildApiRequest()),
            psoFamilyAgreements: this.booking.pso.familyAgreements.agreements.map(family => family.buildApiRequest()),
            insuranceSubscriptionRequests: this.booking.insurance.buildSubscriptionRequests(),
            passengersDetails: this.booking.getPassengersExtraDetails()
        };

        if(options.mobileWalletToken) {
            beginPaymentRequest.mobileWalletToken = options.mobileWalletToken;
            beginPaymentRequest.mobileWalletProvider = options.mobileWalletProvider;
        }

        if(options.cardDetails) {
            beginPaymentRequest.cardDetails = options.cardDetails;
        }


        if(this.booking.invoice.invoiceRequested) {
            beginPaymentRequest = await this.booking.invoice.onBeginPayment(beginPaymentRequest);
        }

        let response = await this.services.airlineWebapi.beginBookingPayment(beginPaymentRequest);

        this._destroyMethodGatheringHandler();
        if(response.threeDSClientMethodGathering) {
            this._methodGatheringHandler = new ThreeDSMethodGatheringHandler(response.threeDSClientMethodGathering, this.booking.services);
            await this._methodGatheringHandler.execute();
        }

        if(response.redirectUrl || response.pspOrderToken) {
            this._pendingPaymentDetails = response
        } else {
            this._pendingPaymentDetails = null;
        }

        return {
            transactionStatus: response.transactionStatus,
            redirectUrl: response.redirectUrl,
            apiErrorCode: response.apiErrorCode,
            pspOrderToken: response.pspOrderToken,
            pspErrorDetails: response.pspErrorDetails ?? []
        };

    }

    async getPaymentStatus(): Promise<IPaymentStatusResponse> {
        if (!this._pendingPaymentDetails) {
            throw new Error('getPaymentStatus called before beginPayment in webapi strategy');
        }

        const response = await this.services.airlineWebapi.getBookingPaymentStatus({
            paymentToken: this._pendingPaymentDetails.paymentToken
        });

        return {
            status: response.status,
            apiErrorCode: response.apiErrorCode,
            pspErrorDetails: response.pspErrorDetails
        };

    }


    onPaymentFinalized(): void {
        this._pendingPaymentDetails = null;
        this._destroyMethodGatheringHandler();
    }

    async finalizePspOrder(): Promise<IFinalizePspOrderResponse> {
        if(!this._pendingPaymentDetails?.paymentToken) {
            throw new Error(`Cannot completePspOrder without a paymentToken`);
        }

        return await this.services.airlineWebapi.finalizePspOrder({
            paymentToken: this._pendingPaymentDetails.paymentToken
        });
    }

    async cancelPspOrder(): Promise<void> {

        if(!this._pendingPaymentDetails?.paymentToken) {
            this.services.logger.error('Attempt to cancel a PSP order without a payment token');
            return;
        }

        try {
            await this.services.airlineWebapi.cancelPspOrder({
                paymentToken: this._pendingPaymentDetails.paymentToken
            });
        } catch (err) {
            this.services.logger.error("cancelPspOrder failed!", err);
        }

    }

}